import React, { Fragment, ReactNode } from 'react';
import useMediaQuery, { SCREEN_SIZES } from '../../hooks/useMediaQuery';
import classnames from 'classnames';
import {
  IconDotsVertical,
  IconHomeLine,
  IconArrowLeft,
  IconChevronRight,
} from '@pentabd/icons';
import Text from '../../atoms/text/Text';

export interface PageHeaderProps {
  banner?: string;
  type?: 'avatar' | 'banner' | 'avatar-banner';
  title?: string;
  description?: string;
  buttons?: JSX.Element[];
  avatar?: ReactNode;
  directory: string[];
  className?: string;
}

const PageHeader = ({
  banner,
  type,
  title,
  description,
  buttons,
  avatar,
  directory = [],
  className,
}: PageHeaderProps) => {
  const isMobile = useMediaQuery(`(max-width: ${SCREEN_SIZES.MD})`);

  return (
    <div className={className}>
      {banner && <img src={banner} className="img-fluid" />}

      {isMobile && (
        <div
          className={classnames('d-flex', 'mt-4', {
            'justify-content-end': type === 'avatar-banner',
            'me-3': type === 'avatar-banner',
            'mb-5': type !== 'avatar-banner',
          })}
        >
          <IconArrowLeft
            width="20"
            height="20"
            fill="dark"
            stroke="subtitle1"
          />
          <p className="m-0 text-subtitle1 fs-sm">Back</p>
        </div>
      )}

      <div
        className={classnames('border-bottom', {
          'pb-3': type !== 'avatar-banner',
          'translate-avatar': type === 'avatar-banner' && isMobile,
          'px-6': (type === 'avatar-banner' || type === 'banner') && !isMobile,
        })}
      >
        {!isMobile && directory.length != 0 && (
          <div
            className={classnames(
              'd-flex',
              'gap-10',
              'py-6',
              'text-subtitle1',
              {
                'translate-directory': type === 'avatar-banner',
              },
            )}
          >
            <IconHomeLine
              width="20"
              height="20"
              fill="dark"
              stroke="subtitle1"
            />
            <IconChevronRight
              width="24"
              height="24"
              fill="dark"
              stroke="subtitle1"
            />

            {directory && directory[0] && (
              <span className="text-capitalize">{directory[0]}</span>
            )}

            {directory && directory.length > 1 && (
              <IconChevronRight
                width="24"
                height="24"
                fill="dark"
                stroke="subtitle1"
              />
            )}

            {directory && directory.length > 2 && (
              <>
                <IconDotsVertical
                  width="24"
                  height="24"
                  fill="fill"
                  stroke="subtitle1"
                  transform="rotate(90)"
                />

                <IconChevronRight
                  width="24"
                  height="24"
                  fill="dark"
                  stroke="subtitle1"
                />
              </>
            )}

            {directory && directory.length > 1 && (
              <span className="text-capitalize">
                {directory[directory.length - 1]}
              </span>
            )}
          </div>
        )}
        <div
          className={classnames('d-flex', 'justify-content-between', {
            'flex-column': isMobile,
          })}
        >
          <div
            className={classnames('d-flex', 'gap-10', {
              'mb-4': isMobile,
              'flex-column': isMobile && type === 'avatar-banner',
            })}
          >
            {(type === 'avatar' || type === 'avatar-banner') && (
              <div
                className={classnames('d-flex', {
                  'translate-avatar': type === 'avatar-banner' && !isMobile,
                  'justify-content-center': type !== 'avatar-banner',
                })}
              >
                {avatar}
              </div>
            )}

            <div className="d-flex flex-column justify-content-center w-100">
              <Text color="title" sizeType="display" size="sm" component="h2">
                {title}
              </Text>

              <Text color="subtitle2" sizeType="fs" size="sm" component="p">
                {description}
              </Text>
            </div>
          </div>

          <div
            className={classnames('d-flex', 'gap-6', 'h-100', 'flex-wrap', {
              'justify-content-end': !isMobile,
            })}
          >
            {buttons?.map((button, index) => (
              <Fragment key={index}>{button}</Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageHeader;
